import { observer } from 'mobx-react';
import { StoreContext } from 'pages/_app';
import { useContext, useRef, useState, useEffect } from 'react';
import Select, { components } from 'react-select';
import { selectStyles, selectStylesDirectory } from 'utils/helpers/styles';
import { toJS } from 'mobx';
import { setCookies } from 'utils/cookies/cookieHandler';
import moment from 'moment-timezone';
import { useRouter } from 'next/router';
import QueryString from 'qs';
import GoogleAutoComplete from 'components/GoogleAutoComplete';
import { formattedAddres, parsedAddress } from 'utils/helpers/addresses';
import Image from 'next/image';
import { SelectDownIcon, SelectUpIcon, AddAddressIcon } from 'assets/img';

const DropdownIndicator = props => {
  const { selectProps } = props;

  return (
    <components.DropdownIndicator {...props}>
      {selectProps.menuIsOpen ? (
        <Image src={SelectUpIcon} alt="Arrow Up" width={16} height={16} />
      ) : (
        <Image src={SelectDownIcon} alt="Arrow Down" width={16} height={16} />
      )}
    </components.DropdownIndicator>
  );
};

const CustomOption = props => {
  const { data, children, innerRef, innerProps } = props;

  return (
    <div
      ref={innerRef}
      {...innerProps}
      className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-[#EEEFFD]"
      style={{
        backgroundColor: props.isFocused ? '#EEEFFD' : 'white',
        color: props.isFocused ? '#575BE8' : '#2D3958',
      }}>
      {data.value === 'enter_new_delivery_address' && (
        <Image src={AddAddressIcon} alt="Add Address Icon" width={16} height={16} />
      )}
      {children}
    </div>
  );
};

const customComponents = {
  DropdownIndicator,
  Option: CustomOption,
};

const customStyles = props => ({
  ...selectStylesDirectory(props),
  control: styles => ({
    // ...styles,
    display: 'flex',
    alignItems: 'center',
    paddingLeft: '10px',
    paddingRight: '10px',
    borderRadius: '8px',
    height: '48px',
    fontSize: '16px',
    fontFamily: 'Inter-Regular',
    cursor: 'pointer',
    justifyContent: 'flex-start', // Align content to the left
  }),
  option: (styles, { isFocused, isSelected }) => ({
    ...styles,
    display: 'flex',
    padding: '12px',
    alignItems: 'center',
    gap: '10px',
    backgroundColor: isFocused ? '#EEEFFD' : isSelected ? '#EEEFFD' : 'white',
    color: isSelected || isFocused ? '#575BE8' : '#2D3958',
    borderLeft: isFocused ? '4px solid #575BE8' : '4px solid transparent',
    cursor: 'pointer',
    fontSize: '16px',
  }),
  valueContainer: styles => ({
    // ...styles,
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  }),
  placeholder: styles => ({
    ...styles,
    display: 'flex',
    alignItems: 'center',
    color: '#2D3958',
    fontSize: '16px',
    lineHeight: '24px',
  }),
  singleValue: styles => ({
    // ...styles,
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '100%',
    textAlign: 'center',
    color: '#2D3958',
    fontSize: '16px',
    lineHeight: '24px',
    alignSelf: 'center',
    margin: '0',
    padding: '0',
  }),
  menu: styles => ({
    ...styles,
    marginTop: '0px',
    border: '2px solid #575BE8',
    borderRadius: '8px',
    boxShadow: 'none',
    overflow: 'hidden',
    marginLeft: '-27px',
    width: 'calc(100% + 27px)',
  }),
  menuPortal: styles => ({
    ...styles,
    zIndex: 9999,
  }),
  dropdownIndicator: styles => ({
    ...styles,
    color: '#2D3958',
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
});

const getAddressState = address => {
  return address
    ? { label: address, value: address }
    : { label: 'Select your address', value: 'Select your address' };
};

const AddressPicker = ({
  fromModal,
  backgroundColor,
  setAddress,
  addressError,
  setAddressError,
  selectedAddress,
  fromRestaurantPage,
  setModal,
  setOpenSavedAddressModal,
}) => {
  const store = useContext(StoreContext);
  const userStore = toJS(store?.userStore);
  const router = useRouter();

  const [showAddressDropdown, setShowAddressDropdown] = useState(true);
  const [internalAddress, setInternalAddress] = useState(null);

  let {
    timeOptionsLoading,
    locationAddresses,
    address,
    currentUser,
    employeeID,
    isAdmin,
    isUncateringUser,
    activeEmployees,
    selectedDate,
    selectedTime,
  } = userStore;

  const [selectedValue, setSelectedValue] = useState(getAddressState(address));

  const selectRef = useRef();
  const id = 'address-picker';

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSelectedValue(getAddressState(address));
      setInternalAddress(address || '');
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [address]);

  const onMenuOpen = () => {
    setTimeout(() => {
      const { focusedOptionRef } = selectRef.current;
      focusedOptionRef && focusedOptionRef.scrollIntoView();
    }, 1);
  };

  const onChangeAddress = addressValue => {
    if (addressValue.value === 'enter_new_delivery_address') {
      if (!isAdmin) {
        //switch input field
        setShowAddressDropdown(false);
      } else {
        setCookies(employeeID, currentUser);
        const fromPage = 'marketplace'; // or dynamically determine this
        const redirectUrl = `${process?.env?.NEXT_PUBLIC_APP_ADMIN_PANEL_LINK}/delivery-info/create?from=${fromPage}`;

        window.location.href = redirectUrl;
      }

      return; // Prevent further execution for this option
    }

    if (setModal && setOpenSavedAddressModal) {
      setModal(false);
      setOpenSavedAddressModal(false);
      setTimeout(() => {
        document.documentElement.removeAttribute('style');
      }, 0);
    }

    const foundAddress = locationAddresses?.find(address => address.id === addressValue.value);

    store.userStore?.setAddressID(foundAddress?.id);
    store.userStore?.setAddressLocationID(foundAddress?.locationId);

    store.userStore?.setAddress(addressValue?.label, parsedAddress(foundAddress?.attributes));
    const account = activeEmployees?.find(
      emp => emp.attributes?.organization_id == foundAddress?.locationId
    );
    const isInvoiceEnabled = account?.attributes.invoice_payments == 'enabled';
    store.userStore?.setLocationPhone(account?.attributes?.organization_contact_number);

    if (isInvoiceEnabled)
      store.userStore.setInvoiceDetails({
        invoice_payments: account?.attributes?.invoice_payments,
        account_billing_address: account?.attributes?.account_billing_address,
        account_billing_emails: account?.attributes?.account_billing_emails,
        account_billing_name: account?.attributes?.account_billing_name,
      });
    else
      store.userStore.setInvoiceDetails({
        invoice_payments: '',
        account_billing_address: '',
        account_billing_emails: '',
        account_billing_name: '',
      });

    setAddress({
      address: addressValue?.label,
      completeAddress: parsedAddress(foundAddress?.attributes),
    });

    setSelectedValue({
      label: addressValue.label,
      value: addressValue.value,
    });

    const date = moment(selectedDate, 'YYYY-MM-DD');
    const dateValue = date.format('YYYY-MM-DD');

    router.replace(
      {
        pathname: router?.pathname,
        query: {
          ...QueryString.parse(router?.query),
          address: addressValue?.label,
          date: dateValue,
          time: selectedTime,
        },
      },
      undefined,
      {
        shallow: true,
      }
    );

    store?.userStore?.setLoader(true);
    store.userStore
      .getAvailableDates({
        address: addressValue.label,
        month: moment().month() + 1,
        year: moment().year(),
        current_date_time: moment().format('YYYY-MM-DD HH:mm'),
      })
      .then(() => {
        store?.userStore?.setLoader(false);
      });
  };

  // const options =
  //   locationAddresses && Array.isArray(locationAddresses)
  //     ? [
  //         ...locationAddresses.map(address => ({
  //           label: formattedAddres(address.attributes),
  //           value: address.id,
  //         })),
  //         {
  //           label: 'Enter new delivery address',
  //           value: 'enter_new_delivery_address',
  //         },
  //       ]
  //     : [
  //         {
  //           label: 'Enter new delivery address',
  //           value: 'enter_new_delivery_address',
  //         },
  //       ];

  const options =
    locationAddresses && Array.isArray(locationAddresses)
      ? [
          ...locationAddresses.map(address => ({
            label: formattedAddres(address.attributes),
            value: address.id,
          })),
          // Only include "Enter new delivery address" if setOpenSavedAddressModal is false
          ...(setOpenSavedAddressModal
            ? []
            : [
                {
                  label: 'Enter new delivery address',
                  value: 'enter_new_delivery_address',
                },
              ]),
        ]
      : setOpenSavedAddressModal
      ? []
      : [
          {
            label: 'Enter new delivery address',
            value: 'enter_new_delivery_address',
          },
        ];

  const addressValue = {
    label: address,
    value: address,
  };

  const handleOnChange = e => {
    if (addressError) setAddressError(false);
    // Clear previously selected city when user types a new address
    store.userStore.setSelectedCity('');
    setAddress({ address: e?.target?.value });
  };

  return (
    <>
      {showAddressDropdown ? (
        <Select
          ref={selectRef}
          menuPortalTarget={document.body}
          menuPosition={fromModal ? 'absolute' : 'fixed'}
          isLoading={timeOptionsLoading}
          onMenuOpen={onMenuOpen}
          isSearchable={false}
          // styles={selectStylesDirectory({ backgroundColor })}
          styles={customStyles({ backgroundColor })}
          components={customComponents}
          placeholder="Address"
          closeMenuOnSelect
          value={selectedValue}
          onChange={onChangeAddress}
          options={options}
          instanceId={id}
        />
      ) : (
        <GoogleAutoComplete
          className="input-light-1 outline-none focus:outline-none address-inputs font-inter-medium my-0 rounded-lg text-md text-primary-black w-full"
          onChange={handleOnChange}
          placeholder="Enter a new address"
          onBlur={e => {
            const queryDate = router?.query?.date;

            store.userStore.getAvailableDates({
              address: e?.target?.value,
              month: queryDate ? moment(queryDate).month() + 1 : moment().month() + 1,
              year: queryDate ? moment(queryDate).year() : moment().year(),
              current_date_time: moment().format('YYYY-MM-DD HH:mm'),
            });
          }}
          value={selectedAddress}
          onPlaceSelected={place => {
            store.userStore?.getAndSetTimezone(place?.formatted_address);

            const _address = place?.address_components?.reduce(
              (seed, { long_name, short_name, types }) => {
                types.forEach(t => {
                  if (t == 'administrative_area_level_1') {
                    seed[t] = short_name; // Set short_name for province
                  } else {
                    seed[t] = long_name; // Set long_name for other types
                  }
                });
                return seed;
              },
              {}
            );

            setAddress({
              address: place?.formatted_address,
              completeAddress: {
                suite: _address?.subpremise,
                street_number: _address?.street_number,
                street_name: _address?.route,
                city: _address?.locality,
                province: _address?.administrative_area_level_1,
                postal_code: _address?.postal_code,
              },
            });

            // store?.userStore?.setAddress(place?.formatted_address, {
            //   suite: _address?.subpremise,
            //   street_number: _address?.street_number,
            //   street_name: _address?.route,
            //   city: _address?.locality,
            //   province: _address?.administrative_area_level_1,
            //   postal_code: _address?.postal_code,
            //   destination_code: '',
            // });
          }}
        />
      )}
    </>
  );
};
export default observer(AddressPicker);
